import { useRouter } from 'next/router';

type ProductQueryValue = string | string[] | number | null | undefined;
type ProductQuery = Record<string, ProductQueryValue>;
type CanonicalProductQuery = Record<string, string | number>;
type ProductFilterRoute = {
  pathname: string;
  query: CanonicalProductQuery;
};

export type ProductFilters = {
  divisi_code: string;
  storage_code: string;
  sort_by_code: string;
  category_lv1: string;
  category_lv2: string;
  category_lv3: string;
  search_query: string;
  page: number;
  size: number;
  product_id: string | null;
};

/**
 * Normalizes Next router query values by selecting the first non-empty string.
 * Duplicate empty params can appear after shallow routing filter changes.
 */
function getFirstNonEmptyString(value: ProductQueryValue): string {
  const values = Array.isArray(value) ? value : [value];
  const firstString = values.find(
    (item): item is string => typeof item === 'string' && item.trim() !== ''
  );

  return firstString ?? '';
}

/**
 * Normalizes numeric query params and falls back when values are empty,
 * invalid, or non-positive.
 */
function getFirstPositiveNumber(
  value: ProductQueryValue,
  defaultValue: number
): number {
  const values = Array.isArray(value) ? value : [value];
  const firstNumber = values
    .map((item) => Number(item))
    .find((item) => Number.isFinite(item) && item > 0);

  return firstNumber ?? defaultValue;
}

export function getProductFilters(query: ProductQuery): ProductFilters {
  const searchKeywordParam = getFirstNonEmptyString(query?.['keyword']);
  const filterParamByDivison = getFirstNonEmptyString(query?.['division']);
  const filterParamByStorage = getFirstNonEmptyString(query?.['storage']);
  const filterParamByCategory1 = getFirstNonEmptyString(query?.['cat1']);
  const filterParamByCategory2 = getFirstNonEmptyString(query?.['cat2']);
  const filterParamByCategory3 = getFirstNonEmptyString(query?.['cat3']);
  const filterParamBySortBy = getFirstNonEmptyString(query?.['sortby']);

  const page = getFirstPositiveNumber(query?.['page'], 1);
  const size = getFirstPositiveNumber(query?.['size'], 25);

  const product_id = getFirstNonEmptyString(query?.['productId']) || null;

  return {
    divisi_code: filterParamByDivison,
    storage_code: filterParamByStorage,
    sort_by_code: filterParamBySortBy,
    category_lv1: filterParamByCategory1,
    category_lv2: filterParamByCategory2,
    category_lv3: filterParamByCategory3,
    search_query: searchKeywordParam,
    page: page,
    size: size,
    product_id,
  };
}

export function toProductQuery(filters: ProductFilters): CanonicalProductQuery {
  const canonicalQuery: CanonicalProductQuery = {
    page: filters.page,
  };

  const stringQueryMap = {
    keyword: filters.search_query,
    division: filters.divisi_code,
    storage: filters.storage_code,
    cat1: filters.category_lv1,
    cat2: filters.category_lv2,
    cat3: filters.category_lv3,
    sortby: filters.sort_by_code,
    productId: filters.product_id,
  };

  Object.entries(stringQueryMap).forEach(([key, value]) => {
    if (value) canonicalQuery[key] = value;
  });

  if (filters.size !== 25) canonicalQuery.size = filters.size;

  return canonicalQuery;
}

export function getCanonicalProductQuery(
  query: ProductQuery,
  overrides: ProductQuery = {}
): CanonicalProductQuery {
  const filters = getProductFilters({ ...query, ...overrides });
  return toProductQuery(filters);
}

export function getProductFilterRoute(
  pathname: string,
  query: ProductQuery,
  overrides: ProductQuery = {}
): ProductFilterRoute {
  return {
    pathname,
    query: getCanonicalProductQuery(query, overrides),
  };
}

export function useProductFilters(): ProductFilters {
  const router = useRouter();
  return getProductFilters(router.query);
}
